import { v4 as uuidv4 } from 'uuid';
import { tokenStorage } from './tokenStorage';

/**
 * "Switch to client view" opens the client session in a new tab and leaves the admin tab alone.
 *
 * The admin tab requests the client tokens, parks them in localStorage under a one-shot handoff key
 * and points the new tab to IMPERSONATION_PATH. Before React mounts, the new tab consumes the handoff,
 * moves the tokens into its own sessionStorage (see tokenStorage) and lands on the client home page.
 */
const IMPERSONATION_PATH = '/impersonate';
const HANDOFF_PARAM = 'handoff';
const HANDOFF_KEY_PREFIX = 'impersonationHandoff:';
const HANDOFF_TTL_MS = 60_000;
const ADMIN_USERS_PATH = '/admin/users';

// Keys of the previous flow, which swapped the admin tokens out of localStorage in every tab.
const LEGACY_ADMIN_ACCESS_TOKEN_KEY = 'adminAccessToken';
const LEGACY_ADMIN_REFRESH_TOKEN_KEY = 'adminRefreshToken';
const LEGACY_ADMIN_PERMISSIONS_KEY = 'adminPermissions';

interface ImpersonationHandoff {
  accessToken: string;
  refreshToken: string;
  createdAt: number;
}

/** Whether the current tab is an admin viewing a client account. */
export const isImpersonating = (): boolean => tokenStorage.isImpersonating();

/**
 * Opens the tab that will host the client session. Must run synchronously in the click handler,
 * before any await, otherwise popup blockers reject it.
 */
export const openImpersonationTab = (): Window | null => window.open('', '_blank');

/** Hands the client tokens over to the tab returned by openImpersonationTab. */
export const handOffToImpersonationTab = (
  tab: Window,
  accessToken: string,
  refreshToken: string,
): void => {
  const handoffId = uuidv4();
  const handoff: ImpersonationHandoff = { accessToken, refreshToken, createdAt: Date.now() };
  localStorage.setItem(HANDOFF_KEY_PREFIX + handoffId, JSON.stringify(handoff));
  tab.location.href = `${window.location.origin}${IMPERSONATION_PATH}?${HANDOFF_PARAM}=${handoffId}`;
};

/**
 * Ends the impersonation of the current tab: closes it when it was opened by the admin tab,
 * otherwise turns it back into an admin tab on the client's profile.
 */
export const endImpersonation = (clientUuid?: string): void => {
  tokenStorage.clearTokens();
  window.close();
  if (!window.closed) {
    window.location.replace(clientUuid ? `${ADMIN_USERS_PATH}?user_uuid=${clientUuid}` : ADMIN_USERS_PATH);
  }
};

/** Reads and deletes a handoff, returning it only while it is still fresh. */
const takeHandoff = (handoffId: string): ImpersonationHandoff | null => {
  const key = HANDOFF_KEY_PREFIX + handoffId;
  const raw = localStorage.getItem(key);
  localStorage.removeItem(key);
  if (!raw) return null;
  try {
    const handoff = JSON.parse(raw) as ImpersonationHandoff;
    return Date.now() - handoff.createdAt <= HANDOFF_TTL_MS ? handoff : null;
  } catch {
    return null;
  }
};

/** Deletes handoffs left behind by tabs closed before they loaded. */
const pruneStaleHandoffs = (): void => {
  Object.keys(localStorage)
    .filter((key) => key.startsWith(HANDOFF_KEY_PREFIX))
    .forEach((key) => {
      try {
        const { createdAt } = JSON.parse(localStorage.getItem(key) || '{}') as Partial<ImpersonationHandoff>;
        if (!createdAt || Date.now() - createdAt > HANDOFF_TTL_MS) localStorage.removeItem(key);
      } catch {
        localStorage.removeItem(key);
      }
    });
};

/**
 * Restores the admin session of a browser still in the previous flow, where the client tokens had
 * replaced the admin ones in localStorage and the admin tokens were parked under legacy keys.
 */
const restoreLegacyAdminSession = (): void => {
  const accessToken = localStorage.getItem(LEGACY_ADMIN_ACCESS_TOKEN_KEY);
  const refreshToken = localStorage.getItem(LEGACY_ADMIN_REFRESH_TOKEN_KEY);
  if (accessToken && refreshToken) {
    tokenStorage.saveTokens(accessToken, refreshToken);
  }
  localStorage.removeItem(LEGACY_ADMIN_ACCESS_TOKEN_KEY);
  localStorage.removeItem(LEGACY_ADMIN_REFRESH_TOKEN_KEY);
  localStorage.removeItem(LEGACY_ADMIN_PERMISSIONS_KEY);
};

/**
 * Runs once before the app mounts. On IMPERSONATION_PATH it turns the tab into an impersonation tab
 * and rewrites the URL to the client home page; an expired or unknown handoff lands on the admin
 * users page instead, with the admin session.
 */
export const bootstrapImpersonation = (): void => {
  restoreLegacyAdminSession();
  if (window.location.pathname === IMPERSONATION_PATH) {
    const handoffId = new URLSearchParams(window.location.search).get(HANDOFF_PARAM);
    const handoff = handoffId ? takeHandoff(handoffId) : null;
    if (handoff) {
      tokenStorage.startImpersonation(handoff.accessToken, handoff.refreshToken);
    }
    window.history.replaceState(null, '', handoff ? '/' : ADMIN_USERS_PATH);
  }
  pruneStaleHandoffs();
};
